import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function AdminDashboard() {
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, pending, confirmed, cancelled

  useEffect(() => {
    if (token) fetchBookings();
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      
      localStorage.setItem('adminToken', data.token);
      setToken(data.token);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const res = await fetch(`${API_URL}/api/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.status === 401 || res.status === 400) {
        handleLogout();
        return;
      }
      const data = await res.json();
      setBookings(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const res = await fetch(`${API_URL}/api/bookings/${id}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        setBookings(bookings.map(b => b._id === id ? { ...b, status: newStatus } : b));
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (!token) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0A0A0A', color: '#FAF6EF' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{
          padding: '40px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, width: '100%', maxWidth: 400
        }}>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, marginBottom: 24, textAlign: 'center', color: '#D4A017' }}>AAROHAN Admin</h2>
          {error && <div style={{ background: 'rgba(216,90,48,0.2)', color: '#D85A30', padding: 12, borderRadius: 4, marginBottom: 20, fontSize: 13, border: '1px solid rgba(216,90,48,0.3)' }}>{error}</div>}
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, color: 'rgba(255,255,255,0.5)' }}>Username</label>
              <input type="text" value={username} onChange={e => setUsername(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: 4, outline: 'none' }} />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, color: 'rgba(255,255,255,0.5)' }}>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: 4, outline: 'none' }} />
            </div>
            <button type="submit" style={{ width: '100%', padding: '14px', background: '#D4A017', color: '#000', border: 'none', borderRadius: 4, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer', fontWeight: 600 }}>
              Access Network
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  const filteredBookings = filter === 'all' ? bookings : bookings.filter(b => b.status === filter);
  
  const pendingCount = bookings.filter(b => b.status === 'pending').length;
  const todayCount = bookings.filter(b => new Date(b.date).toDateString() === new Date().toDateString()).length;

  return (
    <div style={{ minHeight: '100vh', background: '#050505', color: '#EAEAEA', fontFamily: "'DM Sans', sans-serif" }}>
      <header style={{ padding: '20px 40px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(10px)' }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, color: '#D4A017', margin: 0 }}>AAROHAN <span style={{ fontSize: 14, letterSpacing: 4, fontFamily: "'DM Sans', sans-serif", color: 'rgba(255,255,255,0.4)', marginLeft: 10, textTransform: 'uppercase' }}>Command Center</span></h1>
        <button onClick={handleLogout} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', padding: '8px 16px', borderRadius: 4, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', cursor: 'pointer' }}>Disconnect</button>
      </header>

      <div style={{ padding: '40px', maxWidth: 1400, margin: '0 auto' }}>
        
        {/* STATS */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24, marginBottom: 40 }}>
          <StatCard title="Total Bookings" value={bookings.length} />
          <StatCard title="Pending Verifications" value={pendingCount} color={pendingCount > 0 ? '#D4A017' : '#fff'} />
          <StatCard title="Today's Sessions" value={todayCount} />
          <StatCard title="Confirmed" value={bookings.filter(b => b.status === 'confirmed').length} color="#4CAF50" />
        </div>

        {/* CONTROLS */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ fontSize: 20, fontWeight: 400 }}>Recent Bookings</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            {['all', 'pending', 'confirmed', 'cancelled'].map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{
                background: filter === f ? '#D4A017' : 'rgba(255,255,255,0.05)',
                color: filter === f ? '#000' : '#fff',
                border: '1px solid',
                borderColor: filter === f ? '#D4A017' : 'rgba(255,255,255,0.1)',
                padding: '6px 16px', borderRadius: 20, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', cursor: 'pointer'
              }}>
                {f}
              </button>
            ))}
            <button onClick={fetchBookings} style={{ ml: 16, background: 'transparent', border: 'none', color: '#D4A017', cursor: 'pointer', fontSize: 16 }}>↻</button>
          </div>
        </div>

        {/* TABLE */}
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 8, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 1, fontSize: 10 }}>
                <th style={{ padding: 20 }}>Date & Time</th>
                <th style={{ padding: 20 }}>User</th>
                <th style={{ padding: 20 }}>Service</th>
                <th style={{ padding: 20 }}>Code</th>
                <th style={{ padding: 20 }}>Status</th>
                <th style={{ padding: 20 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>Decrypting network data...</td></tr>
              ) : filteredBookings.length === 0 ? (
                <tr><td colSpan="6" style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>No bookings found.</td></tr>
              ) : (
                filteredBookings.map(b => (
                  <tr key={b._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', background: b.status === 'pending' ? 'rgba(212,160,23,0.03)' : 'transparent' }}>
                    <td style={{ padding: '16px 20px' }}>
                      <div style={{ fontWeight: 500, color: '#fff' }}>{new Date(b.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                      <div style={{ color: '#D4A017', fontSize: 11, marginTop: 4 }}>{b.timeSlot}</div>
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <div style={{ color: '#fff' }}>{b.userName}</div>
                      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 4 }}>{b.userPhone}</div>
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <div>{b.service}</div>
                      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 4 }}>{b.price || '-'}</div>
                    </td>
                    <td style={{ padding: '16px 20px', fontFamily: 'monospace', letterSpacing: 2, color: '#fff' }}>{b.confirmationCode}</td>
                    <td style={{ padding: '16px 20px' }}>
                      <span style={{
                        padding: '4px 10px', borderRadius: 4, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1,
                        background: b.status === 'confirmed' ? 'rgba(76,175,80,0.1)' : b.status === 'pending' ? 'rgba(212,160,23,0.1)' : 'rgba(255,255,255,0.05)',
                        color: b.status === 'confirmed' ? '#4CAF50' : b.status === 'pending' ? '#D4A017' : '#888'
                      }}>
                        {b.status}
                      </span>
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      {b.status === 'pending' && (
                        <button onClick={() => updateStatus(b._id, 'confirmed')} style={actionBtnStyle('#4CAF50')}>Confirm</button>
                      )}
                      {(b.status === 'pending' || b.status === 'confirmed') && (
                        <button onClick={() => updateStatus(b._id, 'cancelled')} style={actionBtnStyle('#D85A30', true)}>Cancel</button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const StatCard = ({ title, value, color = '#fff' }) => (
  <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', padding: 24, borderRadius: 8 }}>
    <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: 'rgba(255,255,255,0.4)', marginBottom: 12 }}>{title}</div>
    <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 42, color }}>{value}</div>
  </div>
);

const actionBtnStyle = (color, isSecondary = false) => ({
  background: isSecondary ? 'transparent' : `${color}20`,
  border: `1px solid ${isSecondary ? 'rgba(255,255,255,0.1)' : `${color}40`}`,
  color: isSecondary ? 'rgba(255,255,255,0.6)' : color,
  padding: '6px 12px',
  borderRadius: 4,
  fontSize: 10,
  letterSpacing: 1,
  textTransform: 'uppercase',
  cursor: 'pointer',
  marginRight: 8
});
